import { useMemo, useState } from 'react';

import { useGetAllClassesWithSearchInfinite } from '~entities/classes/api/get-all-classes-with-search';

import Select from '~shared/components/select/select';
import { useDebounce } from '~shared/shadcn/expansions/multiple-selector';

export interface AudienceOption {
  label: string;
  value: string;
}

interface MultiClassAudienceSelectorProps {
  value: Array<AudienceOption>;
  onChange: (value: Array<AudienceOption>) => void;
  disabled?: boolean;
}

export const MultiClassAudienceSelector = ({
  value,
  onChange,
  disabled,
}: MultiClassAudienceSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);

  const {
    data,
    isFetching,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAllClassesWithSearchInfinite({
    name: debouncedSearch,
    showPastClasses: false,
  });

  const options = useMemo(() => {
    const pages = data?.pages ?? [];
    const seen = new Set(value.map((v) => v.value));
    const fromApi: Array<AudienceOption> = [];
    for (const page of pages) {
      const classes = page?.data?.classes ?? [];
      for (const c of classes) {
        if (!seen.has(c._id)) {
          seen.add(c._id);
          fromApi.push({ label: c.title, value: c._id });
        }
      }
    }
    return [...value, ...fromApi];
  }, [data?.pages, value]);

  return (
    <Select
      isClearable
      isMulti
      isDisabled={disabled}
      value={value}
      onChange={(option) => {
        const opt = option as ReadonlyArray<AudienceOption> | null;
        onChange(opt ? [...opt] : []);
      }}
      placeholder='Select classes'
      options={options}
      isLoading={isPending || (isFetching && !isFetchingNextPage)}
      isMenuListFetchingMore={isFetchingNextPage}
      onMenuScrollToBottom={() => {
        if (hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      }}
      onInputChange={(inputValue, { action }) => {
        if (action === 'input-change') {
          setSearchQuery(inputValue);
        }
      }}
    />
  );
};
